/**
 * InfoTooltip Component
 *
 * A reusable tooltip with a "?" icon that shows explanations on hover.
 * Designed for beginner-friendly contextual help throughout the dashboard.
 * Uses React Portal to render at document body level, escaping all stacking contexts.
 */

import React, { useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';

interface InfoTooltipProps {
  text: string;
}

const InfoTooltip: React.FC<InfoTooltipProps> = ({ text }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const iconRef = useRef<HTMLSpanElement>(null);

  const handleMouseEnter = useCallback(() => {
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      const tooltipWidth = 260;

      // Calculate position above the icon, centered
      let left = rect.left + rect.width / 2 - tooltipWidth / 2;
      const top = rect.top - 8; // 8px gap above icon

      // Prevent tooltip from going off-screen on left
      if (left < 10) {
        left = 10;
      }

      // Prevent tooltip from going off-screen on right
      if (left + tooltipWidth > window.innerWidth - 10) {
        left = window.innerWidth - tooltipWidth - 10;
      }

      setPosition({ top, left });
    }
    setIsVisible(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsVisible(false);
  }, []);

  // Render tooltip via Portal to escape stacking contexts
  const tooltipContent = isVisible ? ReactDOM.createPortal(
    <div
      className="info-tooltip-content"
      style={{
        visibility: 'visible',
        opacity: 1,
        top: `${position.top}px`,
        left: `${position.left}px`,
        transform: 'translateY(-100%)'
      }}
    >
      {text}
    </div>,
    document.body
  ) : null;

  return (
    <span
      className="info-tooltip"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span className="info-tooltip-icon" ref={iconRef}>?</span>
      {tooltipContent}
    </span>
  );
};

export default InfoTooltip;
